import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { getUsers, registerUser, removeUser, updateUser } from '../../core/api/endpoints/users/usersApi';
import { RegisterUserModel, UsersModel } from '../../core/domainModels/users/UsersModel';
import { TachoColumnState } from '../../core/domainModels/displayPreferences/tachoColumnState';
import autoMergeLevel1 from 'redux-persist/es/stateReconciler/autoMergeLevel1';
import storage from 'redux-persist/es/storage';
import { fieldName } from '../../core/helpers/aggrid';
import { PersistConfig } from 'redux-persist';

export interface UsersState {
    users: Array<UsersModel>;
    fetchUsersPending: boolean;
    fetchUsersError: string;
    deleteUsersPending: boolean;
    deleteUsersError: string;
    addUsersPending: boolean;
    addUsersError: string;
    updateUsersPending: boolean;
    updateUsersError: string;
    displayPreferences: Array<TachoColumnState>;
    tableFilters: any;
}

const initialState: UsersState = {
    users: [],
    fetchUsersPending: false,
    fetchUsersError: '',
    deleteUsersPending: false,
    deleteUsersError: '',
    addUsersPending: false,
    addUsersError: '',
    updateUsersPending: false,
    updateUsersError: '',
    displayPreferences: [],
    tableFilters: {},
};

const name = 'users';

const fetchUsersThunk = createAsyncThunk(`${name}/fetch`, async (params, thunkAPI) => {
    try {
        return await getUsers();
    } catch (error) {
        return thunkAPI.rejectWithValue(error);
    }
});

const deleteUsersThunk = createAsyncThunk(`${name}/delete`, async (userEmail: string, thunkAPI) => {
    try {
        return await removeUser(userEmail);
    } catch (error) {
        return thunkAPI.rejectWithValue(error);
    }
});

const addUserThunk = createAsyncThunk(`${name}/add`, async (user: RegisterUserModel, thunkAPI) => {
    try {
        return await registerUser(user);
    } catch (error) {
        return thunkAPI.rejectWithValue(error);
    }
});

const updateUserThunk = createAsyncThunk(`${name}/update`, async (user: UsersModel, thunkAPI) => {
    try {
        return await updateUser(user);
    } catch (error) {
        return thunkAPI.rejectWithValue(error);
    }
});

const usersSlice = createSlice({
    name,
    initialState,
    reducers: {
        modifyUsersDisplayPreferences(state, action: PayloadAction<any>) {
            state.displayPreferences = action.payload;
        },
        modifyUsersTableFilters(state, action: PayloadAction<any>) {
            state.tableFilters = action.payload;
        },
    },
    extraReducers: builder => {
        builder
            .addCase(fetchUsersThunk.pending, (state, action) => {
                state.fetchUsersPending = true;
                state.fetchUsersError = '';
            })
            .addCase(fetchUsersThunk.fulfilled, (state, action) => {
                state.fetchUsersError = '';
                state.users = action.payload;
                state.fetchUsersPending = false;
            })
            .addCase(fetchUsersThunk.rejected, (state, action) => {
                state.fetchUsersPending = false;
                state.fetchUsersError = action.payload as string;
            })
            .addCase(deleteUsersThunk.pending, (state, action) => {
                state.deleteUsersPending = true;
                state.deleteUsersError = '';
            })
            .addCase(deleteUsersThunk.fulfilled, (state, action) => {
                state.deleteUsersError = '';
                state.users = state.users.filter(item => item.email !== action.payload);
                state.deleteUsersPending = false;
            })
            .addCase(deleteUsersThunk.rejected, (state, action) => {
                state.deleteUsersPending = false;
                state.deleteUsersError = action.payload as string;
            })
            .addCase(addUserThunk.pending, (state, action) => {
                state.addUsersPending = true;
                state.addUsersError = '';
            })
            .addCase(addUserThunk.fulfilled, (state, action) => {
                state.deleteUsersError = '';
                state.addUsersPending = false;
            })
            .addCase(addUserThunk.rejected, (state, action) => {
                state.addUsersPending = false;
                state.addUsersError = action.payload as string;
            })
            .addCase(updateUserThunk.pending, (state, action) => {
                state.updateUsersPending = true;
                state.updateUsersError = '';
            })
            .addCase(updateUserThunk.fulfilled, (state, action) => {
                const updatedUserIndex = state.users.findIndex(c => c.id === action.payload.id);
                if (updatedUserIndex !== -1) {
                    state.users[updatedUserIndex] = { ...state.users[updatedUserIndex], ...action.payload };
                }
                state.updateUsersPending = false;
            })
            .addCase(updateUserThunk.rejected, (state, action) => {
                state.updateUsersPending = false;
                state.updateUsersError = action.payload as string;
            });
    },
});
export const usersReducerConfig: PersistConfig<UsersState> = {
    key: name,
    storage: storage,
    stateReconciler: autoMergeLevel1,
    whitelist: [fieldName<UsersState>('displayPreferences'), fieldName<UsersState>('tableFilters')],
};
const { reducer, actions } = usersSlice;

const { modifyUsersDisplayPreferences, modifyUsersTableFilters } = actions;
export { reducer as usersReducer };
export {
    fetchUsersThunk,
    deleteUsersThunk,
    addUserThunk,
    updateUserThunk,
    modifyUsersDisplayPreferences,
    modifyUsersTableFilters,
};
